import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, Platform, ActivityIndicator, Keyboard } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { LedgerStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import { DatePicker } from '../../components/common/DatePicker';
import * as ledgerApi from '../../lib/api/ledger';
import { storage } from '../../utils/storage';
import { showSuccessToast, showErrorToast, Alert } from '../../utils/toast';

type AddEditLedgerEntryNavigationProp = StackNavigationProp<LedgerStackParamList, 'AddEditLedgerEntry'>;
type AddEditLedgerEntryRouteProp = RouteProp<LedgerStackParamList, 'AddEditLedgerEntry'>;

const AddEditLedgerEntryScreen: React.FC = () => {
  const navigation = useNavigation<AddEditLedgerEntryNavigationProp>();
  const route = useRoute<AddEditLedgerEntryRouteProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { ledgerId, entryId } = route.params;
  
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ledgerOwnerId, setLedgerOwnerId] = useState<string | null>(null);
  const [ledgerData, setLedgerData] = useState<ledgerApi.LedgerAccount | null>(null);
  
  const isEditing = !!entryId;
  
  const [type, setType] = useState<'received' | 'paid'>('paid');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<Date | undefined>(new Date());
  
  // Participant selection (MANDATORY)
  const [selectedParticipantId, setSelectedParticipantId] = useState<string>('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [manualParticipantName, setManualParticipantName] = useState('');
  const [isEditingSharedTransaction, setIsEditingSharedTransaction] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  // Check if current user is the owner
  const isOwner = user?.userId === ledgerOwnerId;

  // Keyboard visibility listener
  useEffect(() => {
    const keyboardWillShow = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      () => setIsKeyboardVisible(true)
    );
    const keyboardWillHide = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => setIsKeyboardVisible(false)
    );

    return () => {
      keyboardWillShow.remove();
      keyboardWillHide.remove();
    };
  }, []);

  // Load ledger data to check ownership and get participants
  useEffect(() => {
    const loadLedger = async () => {
      const token = await storage.getToken();
      if (!token) return;
      
      try {
        const response = await ledgerApi.getLedgerById(token, ledgerId);
        const ledger = response.ledger;
        setLedgerOwnerId(ledger.userId);
        setLedgerData(ledger);
        console.log('Ledger loaded:', { 
          isShared: ledger.isShared, 
          participantCount: ledger.participants?.length,
          participants: ledger.participants,
          currentUserId: user?.userId,
          ledgerOwnerId: ledger.userId
        });
      } catch (error) {
        console.error('Error loading ledger:', error);
      }
    };
    loadLedger();
  }, [ledgerId]);

  // Load existing entry if editing
  useEffect(() => {
    const loadEntry = async () => {
      if (entryId) {
        const token = await storage.getToken();
        if (!token) return;
        
        try {
          setIsLoading(true);
          // Fetch the specific transaction
          const transaction = await ledgerApi.getTransaction(token, ledgerId, entryId);
          
          // Show transaction as-is (from creator's perspective)
          // No reversal needed - if user created it, they see their perspective
          setType(transaction.type === 'credit' ? 'received' : 'paid');
          setTitle(transaction.title || '');
          setNote(transaction.note || '');
          setAmount(transaction.amount.toString());
          setDate(new Date(transaction.date));
          
          // Load participant data if exists
          if (transaction.participantName) {
            setShowOtherInput(true);
            setManualParticipantName(transaction.participantName);
            setIsEditingSharedTransaction(false); // Manual name can be edited
          } else if (transaction.participantUserIds && transaction.participantUserIds.length > 0) {
            setSelectedParticipantId(transaction.participantUserIds[0]);
            setIsEditingSharedTransaction(true); // Shared with user - can't change participant
          }
        } catch (error) {
          console.error('Error loading entry:', error);
          Alert.alert('Error', 'Failed to load transaction details');
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadEntry();
  }, [entryId, ledgerId]);

  const handleSave = async () => {
    const token = await storage.getToken();
    if (!token) {
      Alert.alert('Error', 'Please log in to continue');
      return;
    }

    // Validation
    // Participant is optional now - user can create entries without tagging anyone
    if (isOwner && showOtherInput && manualParticipantName.trim() === '') {
      // If they opened "Other" but didn't enter a name, just ignore it
      setShowOtherInput(false);
      setManualParticipantName('');
    }
    
    if (!title.trim()) {
      Alert.alert('Error', 'Please enter a title for this entry');
      return;
    }
    
    const amountNum = parseFloat(amount);
    if (!amount.trim() || isNaN(amountNum) || amountNum <= 0) {
      Alert.alert('Error', 'Please enter a valid amount greater than 0');
      return;
    }
    
    if (!date) {
      Alert.alert('Error', 'Please select a date');
      return;
    }

    try {
      setSaving(true);

      // Save transaction from user's perspective (no reversal)
      const transactionType = type === 'received' ? 'credit' : 'debit';
      const transactionData: any = {
        type: transactionType,
        amount: amountNum,
        title: title.trim(),
        note: note.trim() || undefined,
        date: date.toISOString(),
      };
      
      // Add participant data
      // For owners: use selected participant
      // For participants: automatically set to owner
      if (isOwner) {
        if (showOtherInput) {
          transactionData.participantName = manualParticipantName.trim();
        } else if (selectedParticipantId) {
          transactionData.participantUserIds = [selectedParticipantId];
        }
      } else {
        // Non-owner: transaction is with the owner
        transactionData.participantUserIds = [ledgerOwnerId!];
      }

      console.log('💾 Saving transaction:', {
        isEditing,
        transactionData,
        showOtherInput,
        selectedParticipantId,
        manualParticipantName,
      });

      if (isEditing) {
        // Update existing transaction
        await ledgerApi.updateTransaction(token, ledgerId, entryId!, transactionData);
      } else {
        // Create new transaction
        const result = await ledgerApi.createTransaction(token, ledgerId, transactionData);
        console.log('✅ Transaction created:', result);
      }
      
      showSuccessToast(`Entry ${isEditing ? 'updated' : 'added'} successfully!`);
      navigation.goBack();
    } catch (error: any) {
      console.error('Error saving entry:', error);
      showErrorToast(error.message || 'Failed to save entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!entryId) return;
    
    const token = await storage.getToken();
    if (!token) return;
    
    Alert.alert(
      'Delete Transaction',
      'Are you sure you want to delete this transaction?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              setSaving(true);
              await ledgerApi.deleteTransaction(token, ledgerId, entryId);
              showSuccessToast('Transaction deleted successfully');
              navigation.goBack();
            } catch (error: any) {
              console.error('Error deleting transaction:', error);
              showErrorToast(error.message || 'Failed to delete transaction');
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {isEditing ? 'Edit Entry' : 'Add Entry'}
        </ThemedText>
        {isEditing && (
          <TouchableOpacity onPress={handleDelete} style={styles.headerButton}>
            <Ionicons name="trash-outline" size={24} color={theme.error} />
          </TouchableOpacity>
        )}
        {!isEditing && <View style={styles.headerButton} />}
      </View>

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Type *</ThemedText>
            <View style={styles.typeContainer}>
              <TouchableOpacity
                style={[
                  styles.typeButton,
                  { backgroundColor: theme.backgroundSecondary },
                  type === 'received' && styles.typeButtonActiveReceived
                ]}
                onPress={() => setType('received')}
                activeOpacity={0.7}
              >
                <Ionicons 
                  name="arrow-down" 
                  size={20} 
                  color="#0A8A4A"
                />
                <ThemedText style={[
                  styles.typeButtonText,
                  { color: theme.text },
                  type === 'received' && { color: '#000000', fontWeight: '600' }
                ]}>
                  I Received
                </ThemedText>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[
                  styles.typeButton,
                  { backgroundColor: theme.backgroundSecondary },
                  type === 'paid' && styles.typeButtonActivePaid
                ]}
                onPress={() => setType('paid')}
                activeOpacity={0.7}
              >
                <Ionicons 
                  name="arrow-up" 
                  size={20} 
                  color="#D32F2F"
                />
                <ThemedText style={[
                  styles.typeButtonText,
                  { color: theme.text },
                  type === 'paid' && { color: '#000000', fontWeight: '600' }
                ]}>
                  I Paid
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Title *</ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border, borderWidth: 1 }]}
              placeholder="e.g., Advance payment, Marketing - April"
              value={title}
              onChangeText={setTitle}
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Amount *</ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border, borderWidth: 1 }]}
              placeholder="Enter amount"
              value={amount}
              onChangeText={setAmount}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          <View style={styles.formGroup}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Note (Optional)</ThemedText>
            <TextInput
              style={[styles.input, styles.textArea, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border, borderWidth: 1 }]}
              placeholder="Add additional details or description..."
              value={note}
              onChangeText={setNote}
              placeholderTextColor={theme.textSecondary}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          {/* Person Selection - ONLY FOR OWNERS, NOW OPTIONAL */}
          {isOwner && (
            <View style={styles.formGroup}>
              <ThemedText style={[styles.label, { color: theme.text }]}>Person / Transaction with (Optional)</ThemedText>
              <ThemedText style={[styles.hint, { color: theme.textSecondary }]}>
                Select who this transaction is with, or leave empty
              </ThemedText>
            
            {!showOtherInput ? (
              <>
                {/* Show participant list if ledger is shared */}
                {ledgerData?.isShared && ledgerData?.participants && ledgerData.participants.length > 0 ? (
                  <View style={styles.participantDropdown}>
                    {(() => {
                      const filteredParticipants = ledgerData.participants
                        .filter(p => {
                          const shouldShow = p.userId !== user?.userId && p.userId !== ledgerData.userId && p.status === 'accepted';
                          console.log('Participant filter:', { 
                            name: p.name, 
                            userId: p.userId, 
                            status: p.status, 
                            shouldShow,
                            currentUserId: user?.userId,
                            ownerId: ledgerData.userId
                          });
                          return shouldShow;
                        });
                      
                      console.log('Filtered participants count:', filteredParticipants.length);
                      
                      return filteredParticipants.map((participant) => {
                        const isSelected = selectedParticipantId === participant.userId;
                        return (
                          <TouchableOpacity
                            key={participant.userId}
                            style={[
                              styles.participantOption,
                              { backgroundColor: theme.backgroundSecondary, borderColor: theme.border },
                              isSelected && { backgroundColor: theme.accent + '20', borderColor: theme.accent },
                              isEditingSharedTransaction && { opacity: 0.5 }
                            ]}
                            onPress={() => !isEditingSharedTransaction && setSelectedParticipantId(participant.userId)}
                            disabled={isEditingSharedTransaction}
                          >
                            <View style={[
                              styles.radioButton,
                              { borderColor: isSelected ? theme.accent : theme.border },
                              isSelected && { borderColor: theme.accent }
                            ]}>
                              {isSelected && <View style={[styles.radioButtonInner, { backgroundColor: theme.accent }]} />}
                            </View>
                            <ThemedText style={[styles.participantName, { color: theme.text }]}>
                              {participant.name}
                            </ThemedText>
                          </TouchableOpacity>
                        );
                      });
                    })()}
                  </View>
                ) : (
                  <ThemedText style={[styles.hint, { color: theme.textSecondary, marginBottom: 8 }]}>
                    No shared participants. Use "Other" to enter a name.
                  </ThemedText>
                )}
                
                <TouchableOpacity
                  style={[
                    styles.otherButton, 
                    { backgroundColor: theme.backgroundSecondary, borderColor: theme.border },
                    isEditingSharedTransaction && { opacity: 0.5 }
                  ]}
                  onPress={() => {
                    if (!isEditingSharedTransaction) {
                      setShowOtherInput(true);
                      setSelectedParticipantId('');
                    }
                  }}
                  disabled={isEditingSharedTransaction}
                >
                  <Ionicons name="person-add-outline" size={20} color={theme.accent} />
                  <ThemedText style={[styles.otherButtonText, { color: theme.accent }]}>
                    Other (Enter Name)
                  </ThemedText>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border, borderWidth: 1 }]}
                  placeholder="Enter person's name *"
                  value={manualParticipantName}
                  onChangeText={setManualParticipantName}
                  placeholderTextColor={theme.textSecondary}
                  autoFocus
                />
                <TouchableOpacity
                  style={[styles.backToListButton, { marginTop: 8 }]}
                  onPress={() => {
                    setShowOtherInput(false);
                    setManualParticipantName('');
                  }}
                >
                  <Ionicons name="arrow-back" size={16} color={theme.accent} />
                  <ThemedText style={[styles.backToListText, { color: theme.accent }]}>
                    Back to list
                  </ThemedText>
                </TouchableOpacity>
              </>
            )}
          </View>
          )}

          <DatePicker
            value={date}
            onChange={setDate}
            label="Date"
            maximumDate={new Date()}
          />
          
          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity style={[styles.cancelButton, { borderColor: theme.border }]} onPress={handleCancel}>
              <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>Cancel</ThemedText>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.saveButton, { backgroundColor: theme.accent }]} onPress={handleSave} disabled={saving}>
              {saving ? (
                <ActivityIndicator color={theme.textInverse} />
              ) : (
                <ThemedText style={[styles.saveButtonText, { color: theme.textInverse }]}>
                  {isEditing ? 'Save Changes' : 'Save Entry'}
                </ThemedText>
              )}
            </TouchableOpacity>
          </View>
          
          {/* Empty space for keyboard - only when keyboard is visible */}
          {isKeyboardVisible && <View style={{ height: 200 }} />}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  form: {
    padding: 16,
    paddingBottom: 100,
  },
  formGroup: {
    marginBottom: 24,
  },
  hint: {
    fontSize: 12,
    marginBottom: 8,
    marginTop: -4,
  },
  participantDropdown: {
    gap: 8,
    marginBottom: 12,
  },
  participantOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    gap: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  participantName: {
    fontSize: 15,
    fontWeight: '500',
  },
  otherButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  otherButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  backToListButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backToListText: {
    fontSize: 14,
    fontWeight: '500',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
  },
  textArea: {
    minHeight: 100,
    paddingTop: 12,
  },
  typeContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  typeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 8,
  },
  typeButtonActiveReceived: {
    backgroundColor: '#E8F5E9',
    borderWidth: 2,
    borderColor: '#0A8A4A',
  },
  typeButtonActivePaid: {
    backgroundColor: '#FFEBEE',
    borderWidth: 2,
    borderColor: '#D32F2F',
  },
  typeButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  dateDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  dateText: {
    fontSize: 16,
  },
  helperText: {
    fontSize: 12,
    marginTop: 6,
    fontStyle: 'italic',
  },
  datePickerDone: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  datePickerDoneText: {
    fontSize: 14,
    fontWeight: '600',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default AddEditLedgerEntryScreen;
